// Query keys for weight training - can be used in both server and client components
// This file does NOT have "use client" so it can be used in server components

import type {
  ExerciseSearchParams,
  WorkoutHistoryParams,
} from "@/actions/weight-training";

export const wtKeys = {
  all: ["wt"] as const,
  settings: (accountId?: string) =>
    [...wtKeys.all, "settings", accountId ?? "signed-out"] as const,
  plateConfig: () => [...wtKeys.all, "plate-config"] as const,
  // Exercises
  exercises: (params?: ExerciseSearchParams) =>
    [...wtKeys.all, "exercises", params] as const,
  exercise: (id: string) => [...wtKeys.all, "exercise", id] as const,
  exerciseCounts: () => [...wtKeys.all, "exercise-counts"] as const,
  exerciseHistory: (id: string) =>
    [...wtKeys.all, "exercise", id, "history"] as const,
  exercisePRs: (id: string) => [...wtKeys.all, "exercise", id, "prs"] as const,
  previousSets: (exerciseId: string | null, customExerciseId: string | null) =>
    [...wtKeys.all, "previous-sets", exerciseId, customExerciseId] as const,
  // Workouts
  workouts: (params?: WorkoutHistoryParams) =>
    [...wtKeys.all, "workouts", params] as const,
  workout: (id: string) => [...wtKeys.all, "workout", id] as const,
  workoutDetails: (id: string) =>
    [...wtKeys.all, "workout", id, "details"] as const,
  // Routines
  routines: () => [...wtKeys.all, "routines"] as const,
  routineFolders: () => [...wtKeys.all, "routine-folders"] as const,
  routine: (id: string) => [...wtKeys.all, "routine", id] as const,
  routineWithExercises: (id: string) => [...wtKeys.all, "routine", id] as const,
  systemRoutines: () => [...wtKeys.all, "system-routines"] as const,
  // Stats
  dashboardStats: () => [...wtKeys.all, "stats", "dashboard"] as const,
  personalRecords: (limit?: number) =>
    [...wtKeys.all, "stats", "prs", limit] as const,
  volumeByMuscle: () => [...wtKeys.all, "stats", "volume"] as const,
  weeklyVolume: (weeks?: number) =>
    [...wtKeys.all, "stats", "weekly", weeks] as const,
  // Body
  bodyMeasurements: () => [...wtKeys.all, "body", "measurements"] as const,
  progressSummary: () => [...wtKeys.all, "body", "progress"] as const,
  weightHistory: (limit?: number) =>
    [...wtKeys.all, "body", "weight-history", limit] as const,
  // Calendar
  calendar: (year: number, month: number) =>
    [...wtKeys.all, "calendar", year, month] as const,

  // ═══════════════════════════════════════════════════════════════════════════
  // CLIENT-SIDE PERSISTENT STATE (stored in TanStack cache → IndexedDB)
  // ═══════════════════════════════════════════════════════════════════════════

  // Active Workout Session - survives refresh and browser close
  activeWorkout: (accountId?: string) =>
    [
      ...wtKeys.all,
      "client",
      "active-workout",
      accountId ?? "signed-out",
    ] as const,

  // Routine Draft - unsaved changes while editing/creating a routine
  routineDraft: (routineId?: string) =>
    [...wtKeys.all, "client", "routine-draft", routineId ?? "new"] as const,

  // User's UI preferences (not server-synced)
  uiPreferences: () => [...wtKeys.all, "client", "ui-preferences"] as const,
};
